import React from 'react';
import { motion } from 'framer-motion';
import { Zap, BookOpen, Microscope, Car, Settings, Waves, Lightbulb, Trophy } from 'lucide-react';
const volumes = [{
  year: '1°',
  color: 'bg-blue-500'
}, {
  year: '2°',
  color: 'bg-green-500'
}, {
  year: '3°',
  color: 'bg-orange-500'
}, {
  year: '4°',
  color: 'bg-purple-500'
}, {
  year: '5°',
  color: 'bg-slate-500'
}, {
  year: '6°',
  color: 'bg-red-500'
}, {
  year: 'FERIA',
  color: 'bg-yellow-500',
  isSpecial: true
}];
const details = [{
  title: 'TOMO 1 — 1.º Año o nivel equivalente: Introducción a la Física y la Energía',
  desc: 'Método científico, magnitudes, medición y tipos de energía. ✅ Con sistemas de unidades y medición.',
  icon: <Microscope className="text-blue-600" data-id="element-185" />
}, {
  title: 'TOMO 2 — 2.º Año o nivel equivalente: Materia, Calor y Estados de la Naturaleza',
  desc: 'Densidad, estados, cambios, calor específico y transferencia térmica. ✅ Experimentos de cocina de bajo costo.',
  icon: <Waves className="text-green-600" data-id="element-186" />
}, {
  title: 'TOMO 3 — 3.º Año o nivel equivalente: Cinemática y Dinámica',
  desc: 'MRU, MRUV, caída libre y las 3 Leyes de Newton. ✅ Gráficos xt y vt listos para imprimir.',
  icon: <Car className="text-orange-600" data-id="element-187" />
}, {
  title: 'TOMO 4 — 4.º Año o nivel equivalente: Energía, Trabajo y Potencia',
  desc: 'Trabajo mecánico, Ec, Ep, conservación y rendimiento. ✅ Conexión con aplicaciones de energías renovables.',
  icon: <Settings className="text-purple-600" data-id="element-188" />
}, {
  title: 'TOMO 5 — 5.º Año o nivel equivalente: Ondas, Luz y Sonido',
  desc: 'Fenómenos ondulatorios, óptica geométrica y espectro electromagnético. ✅ Efecto Doppler, espejos y lentes resueltos.',
  icon: <Lightbulb className="text-slate-600" data-id="element-189" />
}, {
  title: 'TOMO 6 — 6.º Año o nivel equivalente: Electricidad, Magnetismo y Física Moderna',
  desc: 'Circuitos, inducción, E=mc², cuántica y física nuclear. ✅ Aplicaciones tecnológicas de la Física.',
  icon: <Zap className="text-red-600" data-id="element-190" />
}, {
  title: 'TOMO 7 — MAESTRO DE PROYECTOS Y FERIA DE CIENCIAS',
  desc: '15 proyectos completos paso a paso: Poleas, Hidráulica, Solar, Eólico, Motor Eléctrico, Cohete de Agua y Casa Sustentable. ✅ Guía de presentación al jurado incluida.',
  icon: <Trophy className="text-yellow-600" data-id="element-191" />
}];
export function ProductBreakdown() {
  return <section className="py-8 md:py-20 px-4 max-w-4xl mx-auto" data-id="element-192">
      <div className="text-center mb-6 md:mb-16" data-id="element-193">
        <div className="inline-flex items-center gap-2 text-blue-600 font-black tracking-widest text-sm uppercase mb-4" data-id="element-194">
          <Zap size={18} className="fill-blue-600" data-id="element-195" />
          <span data-id="element-196">Física en Acción</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6 leading-tight" data-id="element-197">
          MEGAPACK FÍSICA EN ACCIÓN™
        </h2>
        <p className="text-slate-600 font-bold text-sm md:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-198">
          7 tomos organizados por año o nivel equivalente. Más de 420 páginas
          con fichas ilustradas, actividades prácticas, experimentos de bajo
          costo, proyectos y evaluaciones completas. Los contenidos trabajan los
          principales ejes de Física presentes en los programas de educación
          secundaria de Latinoamérica y pueden adaptarse al currículo de cada
          país.
        </p>
      </div>

      {/* Visual Volumes Grid */}
      <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-4 mb-8 md:mb-20" data-id="element-199">
        {volumes.map((v, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.05
      }} className={`${v.color} aspect-[3/4] rounded-lg md:rounded-xl flex flex-col items-center justify-center text-white shadow-lg relative overflow-hidden group`} data-id="element-200">
            <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-201" />
            <span className={`font-black ${v.isSpecial ? 'text-[10px] md:text-xs' : 'text-xl md:text-2xl'}`} data-id="element-202">
              {v.year}
            </span>
            {v.isSpecial && <Trophy size={14} className="mt-1" data-id="element-203" />}
          </motion.div>)}
      </div>

      <div className="text-center mb-8 md:mb-12" data-id="element-204">
        <h3 className="text-2xl md:text-4xl font-black text-slate-900" data-id="element-205">
          Lo que <span className="italic text-blue-600" data-id="element-206">recibirás</span>
        </h3>
      </div>

      {/* Details List */}
      <div className="space-y-4" data-id="element-207">
        {details.map((item, i) => <motion.div key={i} initial={{
        opacity: 0,
        x: -10
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.1
      }} className="bg-white p-5 md:p-6 rounded-2xl border border-blue-50 shadow-sm flex gap-4 items-start" data-id="element-208">
            <div className="bg-slate-50 p-3 rounded-xl shrink-0" data-id="element-209">
              {item.icon}
            </div>
            <div data-id="element-210">
              <h4 className="font-black text-slate-900 text-sm md:text-lg mb-1 leading-tight" data-id="element-211">
                {item.title}
              </h4>
              <p className="text-slate-600 text-xs md:text-base leading-relaxed font-medium" data-id="element-212">
                {item.desc}
              </p>
            </div>
          </motion.div>)}
      </div>
    </section>;
}